import { RefObject, useState } from 'react';
import { Dot, ShapeRef } from '../types';
import { useFrame } from '@react-three/fiber';
import { Vector3 } from 'three';
import { useSpringRef, useSprings } from '@react-spring/three';
import { lerp } from '@/utils/lerp';

interface UseDotAnimationProps {
  shapeRef: RefObject<ShapeRef>;
  dots: Dot[];
}

const dummyVector3 = new Vector3();

export function useDotAnimation({ dots, shapeRef }: UseDotAnimationProps) {
  const [isInitialPositioningDone, setIsInitialPositioningDone] =
    useState(false);
  const api = useSpringRef();

  const [springs] = useSprings(dots.length, (index) => {
    shapeRef.current?.dotTargets[index].getWorldPosition(dummyVector3);

    return {
      position: dummyVector3.toArray(),
      scale: 0.05,
      ref: api, // Connect API
    };
  });

  useFrame(() => {
    api.start((dotIndex) => {
      shapeRef.current?.dotTargets[dotIndex].getWorldPosition(dummyVector3);

      return {
        position: dummyVector3.toArray(),
        scale:
          (shapeRef.current?.dotTargets[dotIndex].scale.getComponent(0) ?? 1) *
          lerp(0.005, 0.05, dots[dotIndex].ratio),
        config: { mass: 5, tension: 80, friction: 40 },
        immediate: !isInitialPositioningDone,
      };
    });

    if (!isInitialPositioningDone) {
      setIsInitialPositioningDone(true);
    }

    dots.forEach((dot, dotIndex) => {
      if (!shapeRef.current) {
        console.warn('shapeRef empty');
        return;
      }

      dot.mesh?.rotateZ(-0.01 * Math.sin(dotIndex));

      dot.mesh?.position.fromArray(springs[dotIndex].position.get());

      const scale = springs[dotIndex].scale.get();
      dot.mesh?.scale.set(scale, scale, scale);
    });
  });

  return { isInitialPositioningDone };
}
